import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { agendarLlamada } from "@/lib/api";

const VACIO = {
  nombre: "",
  telefono: "",
  email: "",
  fecha_preferida: "",
  horario_preferido: "",
  motivo: "",
};

const HORARIOS = ["Mañana", "Mediodía", "Tarde"];

export const ScheduleModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(VACIO);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      setForm(VACIO);
      setStatus("idle");
      setErrorMsg("");
    }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const hoy = new Date().toISOString().split("T")[0];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.horario_preferido) {
      setStatus("error");
      setErrorMsg("Selecciona un horario preferido.");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      await agendarLlamada(form);
      setStatus("success");
      toast.success("Llamada agendada. Te llamaremos en la fecha y horario elegidos.");
    } catch {
      setStatus("error");
      setErrorMsg("No se pudo agendar la llamada. Revisa los campos e inténtalo de nuevo.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="schedule-modal"
        className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-md"
      >
        {status === "success" ? (
          <div
            data-testid="schedule-modal-success"
            className="flex flex-col items-center py-8 text-center"
          >
            <CheckCircle2 className="h-14 w-14 text-green-700" strokeWidth={1.4} />
            <h3 className="mt-6 font-serif text-2xl text-tinta">Llamada agendada</h3>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-tinta/65">
              Gracias, {form.nombre.split(" ")[0] || "gracias"}. Te llamaremos en la
              fecha y horario elegidos.
            </p>
            <Button
              data-testid="schedule-modal-success-close"
              onClick={() => onOpenChange(false)}
              className="mt-8 rounded-full px-8"
            >
              Cerrar
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-tinta">
                Agendar llamada
              </DialogTitle>
              <DialogDescription className="text-sm text-tinta/60">
                Elige cuándo te viene mejor y te llamamos para atenderte con calma.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="mt-5 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sm-nombre">Nombre</Label>
                <Input
                  id="sm-nombre"
                  data-testid="schedule-modal-nombre"
                  value={form.nombre}
                  onChange={set("nombre")}
                  placeholder="Tu nombre"
                  required
                  minLength={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sm-telefono">Teléfono</Label>
                <Input
                  id="sm-telefono"
                  data-testid="schedule-modal-telefono"
                  type="tel"
                  value={form.telefono}
                  onChange={set("telefono")}
                  placeholder="+34 600 000 000"
                  required
                  minLength={6}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sm-email">Email</Label>
                <Input
                  id="sm-email"
                  data-testid="schedule-modal-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="tu@email.com"
                  required
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="sm-fecha">Fecha preferida</Label>
                  <Input
                    id="sm-fecha"
                    data-testid="schedule-modal-fecha"
                    type="date"
                    min={hoy}
                    value={form.fecha_preferida}
                    onChange={set("fecha_preferida")}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horario preferido</Label>
                  <Select
                    value={form.horario_preferido}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, horario_preferido: v }))
                    }
                  >
                    <SelectTrigger data-testid="schedule-modal-horario">
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent>
                      {HORARIOS.map((h) => (
                        <SelectItem key={h} value={h} data-testid={`schedule-modal-horario-${h.toLowerCase()}`}>
                          {h}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sm-motivo">Motivo</Label>
                <Textarea
                  id="sm-motivo"
                  data-testid="schedule-modal-motivo"
                  value={form.motivo}
                  onChange={set("motivo")}
                  placeholder="Cuéntanos brevemente el motivo de la llamada"
                  rows={3}
                  required
                  minLength={5}
                />
              </div>
              {status === "error" && (
                <p
                  data-testid="schedule-modal-error"
                  role="alert"
                  className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive"
                >
                  {errorMsg}
                </p>
              )}
              <Button
                type="submit"
                data-testid="schedule-modal-submit"
                disabled={status === "sending"}
                className="w-full rounded-full py-6 text-base"
              >
                {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
                Agendar llamada
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleModal;
